import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
  Button
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import chatService from "@/modules/vendor/service/chat.service";
import { DownloadIcon } from 'lucide-react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description'; // Word Doc
import TableChartIcon from '@mui/icons-material/TableChart'; // Excel
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; // Fallback
import TwoFactorCodeModal from './components/TwoFactorCodeModal';
import Enable2FAModal from './components/Enable2FAModal';



const LoanApplicationForm = ({ open, handleClose, applicationData, twoFactorEnabled: propTwoFactorEnabled }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id: '',
    applicant: '',
    email: '',
    phone: '',
    homeLoanSituation: '',
    creditHistory: '',
    employmentStatus: '',
    numberOfApplicants: '',
    sourceOfIncome: '',
    incomeRange: '',
    expectedPurchasePrice: '',
    depositAmount: '',
    expenseRange: '',
    talkAbout: ''
  });

  // 2FA related state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(propTwoFactorEnabled || false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [showEnable2FAModal, setShowEnable2FAModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isChecking2FA, setIsChecking2FA] = useState(false);
  
  useEffect(() => {
    if (applicationData) {
      const { customer } = applicationData;
      setFormData({
        id: applicationData._id,
        customerId: customer._id,
        applicant: `${applicationData.first_name} ${applicationData.last_name}`,
        email: applicationData.email,
        phone: applicationData.phone_number,
        homeLoanSituation: applicationData.home_loan_situation,
        creditHistory: applicationData.credit_history,
        employmentStatus: applicationData.employment_status,
        numberOfApplicants: applicationData.number_of_applicants,
        sourceOfIncome: applicationData.source_of_income,
        incomeRange: applicationData.income_range,
        expectedPurchasePrice: applicationData.expected_purchase_price,
        depositAmount: applicationData.deposit_amount,
        talkAbout: applicationData.purpose_of_loan,
        expenseRange: applicationData.expense_range || '',
      });
    }
  }, [applicationData]);

  // Update 2FA status when prop changes
  useEffect(() => {
    setTwoFactorEnabled(propTwoFactorEnabled || false);
  }, [propTwoFactorEnabled]);

  const handleClick = async (e) => {
    try {
      await chatService.getVendorConversation(e.target.value);
      navigate("/vendor/inbox", { state: { customerId: applicationData.customer._id } });
    } catch (error) {
      console.error("Error starting conversation:", error);
    }
  };

  const handleFileAccess = (attachment) => {
    if (twoFactorEnabled) {
      setSelectedFile(attachment);
      setShowTwoFactorModal(true);
    } else {
      // Show Enable 2FA modal if 2FA is not enabled
      setShowEnable2FAModal(true);
    }
  };

  const handleTwoFactorSuccess = (fileUrl) => {
    window.open(fileUrl, '_blank');
    setShowTwoFactorModal(false);
    setSelectedFile(null);
  };

  const handleTwoFactorClose = () => {
    setShowTwoFactorModal(false);
    setSelectedFile(null);
  };

  const renderField = (label, value, placeholder = "") => (
    <Grid item xs={12}>
      <Typography variant="subtitle1" gutterBottom>{label}</Typography>
      <TextField
        fullWidth
        placeholder={placeholder}
        variant="outlined"
        size="small"
        value={value}
        disabled
        sx={{
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "#000000",
          },
          mb: 2
        }}
      />
    </Grid>
  );

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 4, maxHeight: '95vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: isMobile ? 2 : 3,
          pt: isMobile ? 2 : 3,
          pb: 0
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            width: '100%',
            fontSize: isMobile ? '1rem' : '1.25rem'
          }}
        >
          Loan Application
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <DialogContent>
        <Grid container spacing={3} sx={{ p: 6 }}>
          {renderField("You want to talk to the broker about?", formData.talkAbout)}
          {renderField("What's your home loan situation?", formData.homeLoanSituation)}
          {renderField("How would you describe your credit history?", formData.creditHistory)}
          {renderField("How do you earn a living?", formData.employmentStatus)}
          {renderField("How many applicants are there?", formData.numberOfApplicants)}
          {renderField("Source of Income", formData.sourceOfIncome)}
          {renderField("What is your income range?", formData.incomeRange)}
          {renderField("What is your expected purchase price?", formData.expectedPurchasePrice)}
          {renderField("How much deposit do you have/saved?", formData.depositAmount)}
          {renderField("How much is expense?", formData.expenseRange)}
          {renderField("Full Name", formData.applicant)}
          {renderField("Email", formData.email)}
          {renderField("Phone Number", formData.phone)}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Attachments
            </Typography>

            {applicationData?.attachments?.length > 0 ? (
              applicationData.attachments.map((attachment, index) => {
                const { file_type, file_url, original_name, s3_url_path } = attachment;

                let IconComponent;
                if (file_type === 'application/pdf') {
                  IconComponent = PictureAsPdfIcon;
                } else if (
                  file_type === 'application/msword' ||
                  file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ) {
                  IconComponent = DescriptionIcon;
                } else if (
                  file_type === 'application/vnd.ms-excel' ||
                  file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ) {
                  IconComponent = TableChartIcon;
                } else {
                  IconComponent = InsertDriveFileIcon;
                }

                return (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => handleFileAccess(attachment)}
                  >
                    <IconComponent
                      color={file_type === 'application/pdf' ? 'error' : 'primary'}
                      sx={{ mr: 2, fontSize: 24 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ 
                          wordBreak: 'break-all', 
                          maxWidth: '100%',
                          fontWeight: 500
                        }}
                      >
                        {original_name}
                      </Typography>
                    </Box>
                    {/* <DownloadIcon size={16} /> */}
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No attachments available.
              </Typography>
            )}
          </Grid>

        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClick}
            value={formData.customerId}
            fullWidth={isMobile}
          >
            Start Conversation
          </Button>
        </Box>

      </DialogContent>
    </Dialog>

    {/* Two-Factor Authentication Modal */}
    {showTwoFactorModal && selectedFile && (
      <TwoFactorCodeModal
        open={showTwoFactorModal}
        onClose={handleTwoFactorClose}
        onSuccess={handleTwoFactorSuccess}
        fileName={selectedFile.original_name}
        s3Path={selectedFile.s3_url_path}
        role="vendor"
      />
    )}

    {/* Enable 2FA Modal */}
    <Enable2FAModal
      open={showEnable2FAModal}
      onClose={() => setShowEnable2FAModal(false)}
    />
    </>
  );
};

export default LoanApplicationForm;
